import "./styles.css";
import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useSearchParams,
} from "react-router-dom";

import PageShell from "./components/PageShell";

import Home from "./pages/Home";
import Shop from "./pages/Shop";
import Item from "./pages/Item";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderConfirmation from "./pages/OrderConfirmation";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Profile from "./pages/Profile";
import MyListings from "./pages/MyListings";
import AddItem from "./pages/AddItem";
import EditItem from "./pages/EditItem";
import Requests from "./pages/Requests";
import MyOrders from "./pages/MyOrders";
import SellerOrders from "./pages/SellerOrders";
import AddRequest from "./pages/AddRequest";
import EditRequest from "./pages/EditRequest";
import RequestDetails from "./pages/RequestDetails";
import OrderDetail from "./pages/OrderDetail";

import {
  getJSON,
  setJSON,
  removeKey,
  setCartUser,
  loadCart,
  saveCart,
} from "./utils/cart";
import { getMe } from "./utils/api";

function EditItemLegacyRedirect() {
  const [params] = useSearchParams();
  const id = params.get("id");
  return id ? (
    <Navigate to={`/edit-item/${id}`} replace />
  ) : (
    <Navigate to="/my-listings" replace />
  );
}

export default function App() {
  const [searchQuery, setSearchQuery] = useState("");

  const initialUser = getJSON("currentUser", null) || getJSON("authUser", null);

  const [currentUser, setCurrentUser] = useState(() => initialUser);

  const [cart, setCart] = useState(() => {
    setCartUser(initialUser?.id ?? null);
    return loadCart();
  });

  const [token, setToken] = useState(() => String(getJSON("token", "") || ""));

  useEffect(() => {
    setCartUser(currentUser?.id ?? null);
    setCart(loadCart());
  }, [currentUser?.id]);

  useEffect(() => {
    saveCart(cart);
  }, [cart]);

  // persist token
  useEffect(() => {
    if (token) setJSON("token", token);
    else removeKey("token");
  }, [token]);

  // load user from token
  useEffect(() => {
    let cancelled = false;

    async function loadMe() {
      if (!token) {
        setCurrentUser(null);
        removeKey("currentUser");
        removeKey("authUser");
        return;
      }

      try {
        const data = await getMe(token);
        const u = data?.user || null;
        if (cancelled) return;

        if (u) {
          const normalized = {
            id: u.userId ?? u.id ?? null,
            name: String(u.username ?? u.name ?? "User").trim(),
            role: String(u.role || "buyer").toLowerCase(),
            email: String(u.email || "").trim(),

            contact: String(u.contact ?? "").trim(),
            address: String(u.address ?? "").trim(),
            joined: u.joined ?? "",
            avatar: String(u.avatar ?? "").trim(),
            created_at: u.created_at ?? "",
            updated_at: u.updated_at ?? "",
          };

          setCurrentUser(normalized);
          setJSON("currentUser", normalized);
          removeKey("authUser");
        } else {
          setCurrentUser(null);
          removeKey("currentUser");
          removeKey("authUser");
        }
      } catch {
        if (cancelled) return;
        setToken("");
        setCurrentUser(null);
        removeKey("currentUser");
        removeKey("authUser");
      }
    }

    loadMe();
    return () => {
      cancelled = true;
    };
  }, [token]);

  function logout() {
    setToken("");
    setCurrentUser(null);
    removeKey("currentUser");
    removeKey("authUser");
    setCartUser(null);
    setCart(loadCart());
  }

  const shellProps = {
    searchQuery,
    setSearchQuery,
    cart,
    setCart,
    currentUser,
    logout,
    token,
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            <PageShell title="SecondAid" {...shellProps}>
              <Home {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/categories"
          element={
            <PageShell title="Shop" {...shellProps}>
              <Shop {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/item/:id"
          element={
            <PageShell title="Item" {...shellProps}>
              <Item {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/cart"
          element={
            <PageShell title="Cart" {...shellProps}>
              <Cart {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/checkout"
          element={
            <PageShell title="Checkout" {...shellProps}>
              <Checkout {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/order-confirmation"
          element={
            <PageShell title="Order Confirmation" {...shellProps}>
              <OrderConfirmation {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/my-orders"
          element={
            <PageShell title="My Orders" {...shellProps}>
              <MyOrders token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/seller-orders"
          element={
            <PageShell title="Seller Orders" {...shellProps}>
              <SellerOrders token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/orders/:id"
          element={
            <PageShell title="Order" {...shellProps}>
              <OrderDetail token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/login"
          element={
            <PageShell title="Login" {...shellProps}>
              <Login setToken={setToken} setCurrentUser={setCurrentUser} />
            </PageShell>
          }
        />

        <Route
          path="/register"
          element={
            <PageShell title="Register" {...shellProps}>
              <Register setToken={setToken} setCurrentUser={setCurrentUser} />
            </PageShell>
          }
        />

        <Route
          path="/profile"
          element={
            <PageShell title="Profile" {...shellProps}>
              <Profile currentUser={currentUser} onLogout={logout} />
            </PageShell>
          }
        />

        <Route
          path="/my-listings"
          element={
            <PageShell title="My Listings" {...shellProps}>
              <MyListings currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/add-item"
          element={
            <PageShell title="Add Item" {...shellProps}>
              <AddItem token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route path="/edit-item" element={<EditItemLegacyRedirect />} />

        <Route
          path="/edit-item/:id"
          element={
            <PageShell title="Edit Item" {...shellProps}>
              <EditItem token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/requests"
          element={
            <PageShell title="Buyer Requests" {...shellProps}>
              <Requests {...shellProps} />
            </PageShell>
          }
        />

        <Route
          path="/add-request"
          element={
            <PageShell title="Create Request" {...shellProps}>
              <AddRequest token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/edit-request/:id"
          element={
            <PageShell title="Edit Request" {...shellProps}>
              <EditRequest token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="/requests/:id"
          element={
            <PageShell title="Request" {...shellProps}>
              <RequestDetails token={token} currentUser={currentUser} />
            </PageShell>
          }
        />

        <Route
          path="*"
          element={
            <PageShell title="Not Found" {...shellProps}>
              <div className="container pagePadBottom">
                <div className="card">
                  <div style={{ fontWeight: 900, fontSize: 18 }}>
                    Page not found
                  </div>
                  <div className="muted" style={{ marginTop: 6 }}>
                    That route doesn't exist.
                  </div>
                </div>
              </div>
            </PageShell>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
